// 観測ログ: STT / 構造化 API の latency + チャー数 + token 数 + 概算コストを蓄積。
// CLAUDE.md §6 原則「計測がそのまま成果物」の実装。
// localStorage に直近 LIMIT 件まで保持。CSV エクスポート可能。

const STORAGE_KEY = "audio-form:metrics:v1";
const LIMIT = 200;

// 概算コスト単価（変動する可能性あり、PoC の参考値）
// ElevenLabs Scribe v2: 約 $0.40/hour = $0.0067/min ≒ $0.000111 / 1000ms
const ELEVENLABS_USD_PER_MS = 0.0067 / 60_000;
// ElevenLabs TTS Flash v2.5: ~$0.30 / 1k chars (Creator プラン換算の概算、PoC 参考値)
const ELEVENLABS_TTS_USD_PER_CHAR = 0.3 / 1000;
// OpenAI gpt-4o-mini: $0.150 / 1M input tokens, $0.600 / 1M output tokens
const OPENAI_INPUT_USD_PER_TOKEN = 0.15 / 1_000_000;
const OPENAI_OUTPUT_USD_PER_TOKEN = 0.6 / 1_000_000;

export type SttMetric = {
  kind: "stt";
  provider: "elevenlabs";
  recordedAt: number;
  // I3: aggregateSummary の cross-session 集計汚染防止用。省略時は recordedAt の
  // time-window で fallback 判定する (backward compat、既存 fixture は付与不要)。
  sessionId?: string;
  audioDurationMs: number | null;
  serverDurationMs: number | null;
  roundTripMs: number;
  chars: number;
  model: string;
  estimatedCostUsd: number;
};

export type StructureMetric = {
  kind: "structure";
  provider: "openai";
  recordedAt: number;
  // I3: SttMetric.sessionId と同じ役割 (aggregateSummary の session 判別用)。
  sessionId?: string;
  // "name-rank" は氏名の順位付け (spec 2026-08-20 §5.5)。実体は同じ proxy への LLM
  // 1 往復なので structure として計上する。**summary.structure の読み方は変わる** —
  // 氏名 step の LLM 往復が 1 発話あたり 1 回から 2 回になるので count と
  // totalEstimatedCostUsd が増え、prompt が長いぶん meanRoundTripMs も動く。
  // フィールド名は据え置きなので、パイロット間の比較でここを同じ軸として扱ってはいけない
  // (interactions.bargeinCount と同じ扱い)。1 件ずつの内訳は localStorage の rolling
  // buffer と preflight.name_ranked.ms にある。
  questionType: "scale" | "short" | "long" | "intent" | "name" | "name-rank";
  serverDurationMs: number | null;
  roundTripMs: number;
  inputTokens: number;
  outputTokens: number;
  totalTokens: number;
  model: string;
  estimatedCostUsd: number;
};

export type TtsMetric = {
  kind: "tts";
  provider: "elevenlabs";
  recordedAt: number;
  // I3: SttMetric.sessionId と同じ役割 (aggregateSummary の session 判別用)。
  sessionId?: string;
  chars: number;
  serverDurationMs: number | null;
  roundTripMs: number;
  model: string;
  estimatedCostUsd: number;
};

export type Metric = SttMetric | StructureMetric | TtsMetric;

export type MetricsSummary = {
  count: number;
  sttCount: number;
  structureCount: number;
  ttsCount: number;
  totalEstimatedCostUsd: number;
  sttMeanRoundTripMs: number;
  sttP95RoundTripMs: number;
  structureMeanRoundTripMs: number;
  structureP95RoundTripMs: number;
  ttsMeanRoundTripMs: number;
  ttsP95RoundTripMs: number;
};

export function estimateSttCostUsd(audioDurationMs: number | null): number {
  if (audioDurationMs == null || audioDurationMs <= 0) return 0;
  return audioDurationMs * ELEVENLABS_USD_PER_MS;
}

export function estimateStructureCostUsd(inputTokens: number, outputTokens: number): number {
  return inputTokens * OPENAI_INPUT_USD_PER_TOKEN + outputTokens * OPENAI_OUTPUT_USD_PER_TOKEN;
}

export function estimateTtsCostUsd(chars: number): number {
  if (chars <= 0) return 0;
  return chars * ELEVENLABS_TTS_USD_PER_CHAR;
}

function safeLoad(): Metric[] {
  if (typeof localStorage === "undefined") return [];
  const raw = localStorage.getItem(STORAGE_KEY);
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as Metric[]) : [];
  } catch {
    return [];
  }
}

function safeSave(metrics: Metric[]): void {
  if (typeof localStorage === "undefined") return;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(metrics));
  } catch {
    // QuotaExceeded 等は静かに無視
  }
}

export function logMetric(metric: Metric): void {
  // console には常に構造化ログを出す（PoC では DevTools が一次情報源）
  console.log(`[metrics:${metric.kind}]`, metric);
  const all = safeLoad();
  all.push(metric);
  // 上限超過分は古い方から捨てる
  const trimmed = all.length > LIMIT ? all.slice(all.length - LIMIT) : all;
  safeSave(trimmed);
}

export function getMetrics(): Metric[] {
  return safeLoad();
}

export function clearMetrics(): void {
  if (typeof localStorage === "undefined") return;
  localStorage.removeItem(STORAGE_KEY);
}

function percentile(values: number[], p: number): number {
  if (values.length === 0) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  const idx = Math.min(sorted.length - 1, Math.floor((sorted.length - 1) * p));
  return sorted[idx];
}

function mean(values: number[]): number {
  if (values.length === 0) return 0;
  return values.reduce((a, b) => a + b, 0) / values.length;
}

export function getSummary(): MetricsSummary {
  const all = getMetrics();
  const stt = all.filter((m): m is SttMetric => m.kind === "stt");
  const structure = all.filter((m): m is StructureMetric => m.kind === "structure");
  const tts = all.filter((m): m is TtsMetric => m.kind === "tts");
  const sttRtt = stt.map((m) => m.roundTripMs);
  const structRtt = structure.map((m) => m.roundTripMs);
  const ttsRtt = tts.map((m) => m.roundTripMs);
  const totalCost = all.reduce((sum, m) => sum + m.estimatedCostUsd, 0);
  return {
    count: all.length,
    sttCount: stt.length,
    structureCount: structure.length,
    ttsCount: tts.length,
    totalEstimatedCostUsd: totalCost,
    sttMeanRoundTripMs: mean(sttRtt),
    sttP95RoundTripMs: percentile(sttRtt, 0.95),
    structureMeanRoundTripMs: mean(structRtt),
    structureP95RoundTripMs: percentile(structRtt, 0.95),
    ttsMeanRoundTripMs: mean(ttsRtt),
    ttsP95RoundTripMs: percentile(ttsRtt, 0.95),
  };
}

export function exportMetricsCsv(): string {
  const all = getMetrics();
  const header = [
    "kind",
    "provider",
    "recordedAt",
    "questionType",
    "audioDurationMs",
    "serverDurationMs",
    "roundTripMs",
    "chars",
    "inputTokens",
    "outputTokens",
    "totalTokens",
    "model",
    "estimatedCostUsd",
  ].join(",");
  const rows = all.map((m) => {
    const row: Array<string | number> = [
      m.kind,
      m.provider,
      new Date(m.recordedAt).toISOString(),
      m.kind === "structure" ? m.questionType : "",
      m.kind === "stt" ? String(m.audioDurationMs ?? "") : "",
      m.serverDurationMs ?? "",
      m.roundTripMs,
      m.kind === "stt" ? m.chars : m.kind === "tts" ? m.chars : "",
      m.kind === "structure" ? m.inputTokens : "",
      m.kind === "structure" ? m.outputTokens : "",
      m.kind === "structure" ? m.totalTokens : "",
      m.model,
      m.estimatedCostUsd.toFixed(6),
    ];
    return row.join(",");
  });
  return [header, ...rows].join("\n");
}
